import React, { useEffect, useState, useContext, useMemo } from "react";
import { UserContext } from "../context/UserContext";
import api from "../api";

// --- Iconset (emoji-basiert, keine zusätzlichen Dependencies) ---
export const ICON_SET = [
  { value: "", label: "Kein Icon" },
  { value: "🏠", label: "Home / Dashboard" },
  { value: "📋", label: "Übersicht / Liste" },
  { value: "🔎", label: "Suche" },
  { value: "🔒", label: "Passwort / Sicherheit" },
  { value: "⏳", label: "Ausstehend / Pending" },
  { value: "👤", label: "Benutzer" },
  { value: "👥", label: "Mitglieder / Gruppe" },
  { value: "🛡️", label: "Rechte / Sicherheit" },
  { value: "🧩", label: "Rollen / Module" },
  { value: "🧭", label: "Navigation / Sidebar" },
  { value: "🌐", label: "Social / Web" },
  { value: "📣", label: "Broadcast / Rundmail" },
  { value: "🏅", label: "Abzeichen / Badge" },
  { value: "⭐", label: "Favorit / Punkte" },
  { value: "📈", label: "Statistik / Leaderboard" },
  { value: "🏆", label: "Leaderboard / Erfolg" },
  { value: "🗄️", label: "Archiv" },
  { value: "🕑", label: "Historie / Verlauf" },
  { value: "⏱️", label: "Cron / Timer" },
  { value: "⚙️", label: "Einstellungen" },
  { value: "🧰", label: "Werkzeuge / Admin" },
  { value: "🧪", label: "Experimente / Tests" },
  { value: "💬", label: "Chat / Kommunikation" },
  { value: "📨", label: "Nachrichten / Inbox" },
  { value: "📊", label: "Berichte / Charts" }
];

function IconSelect({ value, onChange, style }) {
  return (
    <select
      value={value ?? ""}
      onChange={(e) => onChange(e.target.value)}
      style={{ width: 140, fontSize: 14, padding: "2px 6px", ...(style || {}) }}
    >
      {ICON_SET.map((opt) => (
        <option key={opt.value || "none"} value={opt.value}>
          {opt.value ? `${opt.value} ${opt.label}` : opt.label}
        </option>
      ))}
    </select>
  );
}

// MENU_ITEMS is now only used for fallback labels
const MENU_ITEMS = [
  { key: "dashboard", label: "Dashboard" },
  { key: "search", label: "Mitgliedersuche" },
  { key: "password", label: "Passwort ändern" },
  { key: "news-feed", label: "Neuigkeiten" },
  { key: "punkte-spiele", label: "Punkte-Spiele" },
  { key: "pending", label: "Ausstehende Anmeldungen" },
  { key: "users", label: "Benutzerverwaltung" },
  { key: "permissions", label: "Rechteverwaltung" },
  { key: "roles", label: "Rollenverwaltung" },
  { key: "sidebar-config", label: "Sidebar-Konfiguration" },
  { key: "seasonal-events", label: "Saisonale Ereignisse" },
  { key: "social-platforms", label: "Social-Plattformen" },
  { key: "broadcast", label: "Rundmails" },
  { key: "badges", label: "Abzeichen" },
  { key: "points", label: "Meine Punkte" },
  { key: "admin-points-history", label: "Punktehistorie (Admin)" },
  { key: "cronjobs", label: "CronJobs" },
  { key: "live-settings", label: "Live-Status Prüfung" },
  { key: "leaderboard", label: "Leaderboard" },
  { key: "weeklyPointsArchive", label: "Weekly-Points Archiv" },
];

export default function AdminSidebarConfig() {
  // --- State ---
  const [groups, setGroups] = useState([]);
  const [groupEdit, setGroupEdit] = useState({ key: "", label: "", icon: "", order: 100 });
  const [groupEditMode, setGroupEditMode] = useState(false);

  const [roles, setRoles] = useState([]);
  const [config, setConfig] = useState({});
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState("");
  const { user } = useContext(UserContext);

  // Build a map for quick role index lookup
  const roleIndexByName = useMemo(() => new Map(roles.map((r, i) => [r.name, i])), [roles]);
  const userMax = useMemo(() => {
    const indices = (user?.roles || []).map((r) => roleIndexByName.get(r) ?? -1);
    return Math.max(-1, ...indices);
  }, [user, roleIndexByName]);


  // --- Effects ---
  // Load roles, groups, and sidebar items dynamically
  useEffect(() => {
    const load = async () => {
      setLoading(true);
      setLoadError("");
      try {
        const [rolesRes, cfgRes] = await Promise.all([
          api.get("/roles"),
          api.get("/sidebar-config"),
        ]);

        const sortedRoles = [...rolesRes.data].sort(
          (a, b) => (a.order ?? 0) - (b.order ?? 0)
        );
        setRoles(sortedRoles);

        // Groups
        const groupList = (Array.isArray(cfgRes.data) ? cfgRes.data : [])
          .filter((e) => e.type === "group")
          .sort((a, b) => (a.order ?? 100) - (b.order ?? 100));
        setGroups(groupList);

        // Items: dynamically merge all sidebar items (type !== 'group')
        const itemsFromBackend = (Array.isArray(cfgRes.data) ? cfgRes.data : [])
          .filter((e) => e.type !== "group");

        // Use MENU_ITEMS for fallback labels
        const labelMap = new Map(MENU_ITEMS.map((i) => [i.key, i.label]));

        const obj = {};
        for (const entry of itemsFromBackend) {
          obj[entry.key] = {
            roles: entry.roles || [],
            label: entry.label || labelMap.get(entry.key) || entry.key,
            icon: entry.icon || "",
            order: typeof entry.order === "number" ? entry.order : 100,
            group: entry.group || "",
            path: entry.path || "",
          };
        }
        setConfig(obj);
      } catch (e) {
        console.error(e);
        setLoadError(e?.response?.data?.error || e.message || "Fehler beim Laden der Daten");
      }
      finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  // --- Handlers: Groups ---
  const handleGroupEdit = (group) => {
    setGroupEdit({
      key: group.key || "",
      label: group.label || "",
      icon: group.icon || "",
      order: typeof group.order === "number" ? group.order : 100,
    });
    setGroupEditMode(true);
  };

  const handleGroupDelete = async (key) => {
    if (!window.confirm("Gruppe wirklich löschen?")) return;
    try {
      await api.post("/sidebar-config/delete-group", { key });
      setGroups((gs) => gs.filter((g) => g.key !== key));
    } catch (e) {
      alert("Fehler beim Löschen: " + (e?.response?.data?.error || e.message));
    }
  };

  const handleGroupSave = async () => {
    try {
      await api.post("/sidebar-config/save-group", { group: groupEdit });
      setGroupEditMode(false);
      setGroupEdit({ key: "", label: "", icon: "", order: 100 });
      // Reload groups
      const res = await api.get("/sidebar-config");
      const list = (Array.isArray(res.data) ? res.data : [])
        .filter((e) => e.type === "group")
        .sort((a, b) => (a.order ?? 100) - (b.order ?? 100));
      setGroups(list);
    } catch (e) {
      setLoadError(e?.response?.data?.error || e.message || "Fehler beim Speichern");
    }
  };

  // --- Handlers: Items ---
  const handleRoleChange = (itemKey, role, checked) => {
    setConfig((cfg) => {
      const entry = cfg[itemKey] || { roles: [], label: "", icon: "", order: 100, group: "" };
      const roles = entry.roles || [];
      return {
        ...cfg,
        [itemKey]: {
          ...entry,
          roles: checked ? [...new Set([...roles, role])] : roles.filter((r) => r !== role),
        },
      };
    });
  };

  const handleLabelChange = (itemKey, value) => {
    setConfig((cfg) => ({
      ...cfg,
      [itemKey]: {
        ...cfg[itemKey],
        label: value,
      },
    }));
  };

  const handlePathChange = (itemKey, value) => {
    setConfig((cfg) => ({
      ...cfg,
      [itemKey]: {
        ...cfg[itemKey],
        path: value,
      },
    }));
  };

  const handleIconChange = (itemKey, value) => {
    setConfig((cfg) => ({
      ...cfg,
      [itemKey]: {
        ...cfg[itemKey],
        icon: value,
      },
    }));
  };

  const handleOrderChange = (itemKey, value) => {
    const n = parseInt(value, 10);
    setConfig((cfg) => ({
      ...cfg,
      [itemKey]: {
        ...cfg[itemKey],
        order: Number.isNaN(n) ? 100 : n,
      },
    }));
  };

  const handleGroupSelect = (itemKey, value) => {
    setConfig((cfg) => ({
      ...cfg,
      [itemKey]: {
        ...cfg[itemKey],
        group: value,
      },
    }));
  };

  const saveConfig = async () => {
    setLoading(true);
    try {
      // Save ALL items currently in config (not just MENU_ITEMS)
      const filtered = {};
      for (const [key, entry] of Object.entries(config)) {
        filtered[key] = {
          roles: (entry.roles || []).filter(
            (roleName) => (roleIndexByName.get(roleName) ?? Infinity) <= userMax
          ),
          label: entry.label ?? "",
          icon: entry.icon ?? "",
          order: typeof entry.order === "number" && !Number.isNaN(entry.order)
            ? entry.order
            : 100,
          group: entry.group || "",
          path: entry.path || "",
        };
      }

      await api.post("/sidebar-config", { config: filtered });
      // Optional: kurzer visueller Hinweis
      setLoadError("");
    } catch (e) {
      setLoadError(e?.response?.data?.error || e.message || "Fehler beim Speichern");
    } finally {
      setLoading(false);
    }
  };

  // --- Render ---
  return (
    <div className="home-page">
      <section style={{ width:'100vw', margin:0, padding:0, position:'relative', left:'-120px' }}>
        <h1 className="hero-title" style={{ textAlign:'center', margin:'0 0 8px' }}>Sidebar-Konfiguration</h1>
        <p className="hero-desc" style={{ margin:'0 auto 24px', textAlign:'center', maxWidth:760 }}>Stelle ein, welche Rollen welche Menüpunkte sehen dürfen.</p>
      </section>
      {loadError && (
        <div style={{ color: 'tomato', margin: '8px 0', padding: '8px 12px', background: 'rgba(255,99,71,0.1)', border: '1px solid rgba(255,99,71,0.4)', borderRadius: 6 }}>
          {loadError}
        </div>
      )}
      {loading && (
        <div style={{ opacity: 0.8, marginBottom: 8 }}>Lade Daten …</div>
      )}

      {/* Gruppenverwaltung */}
      <section style={{ width: "100%", margin: "0 0 32px 0", padding: 0 }}>
        <h2 style={{ fontSize: 20, margin: "16px 0 8px 0" }}>Gruppenverwaltung</h2>

        <div style={{ marginBottom: 12 }}>
          <button onClick={() => {
            setGroupEdit({ key: "", label: "", icon: "", order: 100 });
            setGroupEditMode(true);
          }}>+ Neue Gruppe</button>
        </div>

        {groupEditMode && (
          <div style={{ border: "1px solid #ddd", padding: 12, borderRadius: 8, marginBottom: 12 }}>
            <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
              <input
                type="text"
                placeholder="Key (eindeutig)"
                value={groupEdit.key}
                onChange={(e) => setGroupEdit((g) => ({ ...g, key: e.target.value }))}
                style={{ width: 180 }}
              />
              <input
                type="text"
                placeholder="Label"
                value={groupEdit.label}
                onChange={(e) => setGroupEdit((g) => ({ ...g, label: e.target.value }))}
                style={{ width: 220 }}
              />
              <IconSelect
  value={groupEdit.icon}
  onChange={(val) => setGroupEdit((g) => ({ ...g, icon: val }))}
  style={{ width: 140 }}
/>
              <input
                type="number"
                min={0}
                max={999}
                placeholder="Reihenfolge"
                value={groupEdit.order}
                onChange={(e) =>
                  setGroupEdit((g) => ({
                    ...g,
                    order: Number.isNaN(parseInt(e.target.value, 10))
                      ? 100
                      : parseInt(e.target.value, 10),
                  }))
                }
                style={{ width: 100, textAlign: "center" }}
              />
              <button onClick={handleGroupSave}>Speichern</button>
              <button onClick={() => setGroupEditMode(false)} style={{ marginLeft: 6 }}>
                Abbrechen
              </button>
            </div>
          </div>
        )}

        <table style={{ width: "100%", borderCollapse: "collapse", marginBottom: 12 }}>
          <thead>
            <tr>
              <th style={{ textAlign: "left" }}>Gruppenname</th>
              <th>Icon</th>
              <th>Reihenfolge</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {groups.map((g) => (
              <tr key={g.key}>
                <td>{g.label || g.key}</td>
                <td style={{ fontSize: 22, textAlign: "center" }}>{g.icon}</td>
                <td style={{ textAlign: "center" }}>{g.order}</td>
                <td>
                  <button onClick={() => handleGroupEdit(g)}>Bearbeiten</button>
                  <button onClick={() => handleGroupDelete(g.key)} style={{ marginLeft: 8 }}>
                    Löschen
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>

      {/* Rollen-/Sichtbarkeitsmatrix */}
      <section style={{ width: "100%", margin: 0, padding: 0 }}>
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead>
            <tr>
              <th style={{ textAlign: "left" }}>Menüpunkt</th>
              <th>Reihenfolge</th>
              <th style={{ textAlign: "left" }}>Gruppe</th>
              <th style={{ textAlign: "left" }}>Label</th>
              <th style={{ textAlign: "left" }}>Pfad</th>
              <th>Icon</th>
              {roles.map((role) => (
                <th key={role.name}>{role.label}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {Object.entries(config)
              .sort(([, a], [, b]) => (a.order ?? 100) - (b.order ?? 100))
              .map(([key, entry]) => (
                <tr key={key}>
                  <td style={{ width: 220 }}>{entry.label || key}</td>
                  <td style={{ textAlign: "center", width: 90 }}>
                    <input
                      type="number"
                      value={entry.order ?? 100}
                      min={0}
                      max={999}
                      onChange={(e) => handleOrderChange(key, e.target.value)}
                      style={{ width: 64, fontSize: 14, textAlign: "center" }}
                    />
                  </td>
                  <td style={{ width: 240 }}>
                    <select
                      value={entry.group ?? ""}
                      onChange={(e) => handleGroupSelect(key, e.target.value)}
                      style={{ width: "100%", fontSize: 14, padding: "2px 6px" }}
                    >
                      <option value="">(keine Gruppe)</option>
                      {groups.map((g) => (
                        <option key={g.key} value={g.key}>
                          {g.label || g.key}
                        </option>
                      ))}
                    </select>
                  </td>
                  <td>
                    <input
                      type="text"
                      value={entry.label}
                      onChange={(e) => handleLabelChange(key, e.target.value)}
                      style={{ width: "100%", fontSize: 14, padding: "2px 6px" }}
                      placeholder={key}
                    />
                  </td>
                  <td>
                    <input
                      type="text"
                      placeholder="/route/pfad"
                      value={entry.path || ""}
                      onChange={(e) => handlePathChange(key, e.target.value)}
                      style={{ width: "100%", fontSize: 14, padding: "2px 6px" }}
                    />
                  </td>
                  <td style={{ textAlign: "center", width: 140 }}>
                    <IconSelect
                      value={entry.icon ?? ""}
                      onChange={(val) => handleIconChange(key, val)}
                    />
                  </td>
                  {roles.map((role) => {
                    const roleIdx = roleIndexByName.get(role.name) ?? Infinity;
                    const disabled = loading || roleIdx > userMax;
                    const checked = (entry.roles || []).includes(role.name);
                    return (
                      <td key={role.name} style={{ textAlign: "center" }}>
                        <input
                          type="checkbox"
                          checked={!!checked}
                          onChange={(e) => handleRoleChange(key, role.name, e.target.checked)}
                          disabled={disabled}
                        />
                      </td>
                    );
                  })}
                </tr>
              ))}
          </tbody>
        </table>

        <div style={{ marginTop: 24 }}>
          <button className="hero-btn-primary" onClick={saveConfig} disabled={loading}>
            Sidebar-Konfiguration speichern
          </button>
        </div>
      </section>
    </div>
  );
}
